import React, { useCallback, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  AlertTriangle,
  Building2,
  CheckCircle2,
  Link2,
  RefreshCw,
  RotateCcw,
  Search,
  Users,
  X,
} from 'lucide-react';
import { PageHeader } from '../../components/common/PageHeader';
import {
  AdviserRole,
  AdviserRow,
  BrokerCandidate,
  useCrmMappings,
} from '../../hooks/useCrmMappings';
import {
  CrmConnectionStatus,
  useCrmConnection,
} from '../../hooks/useCrmConnection';

type OutletCtx = {
  userData?: { companyId?: string; role?: string; canSeeAllOffices?: boolean; allowedEntityCodes?: string[] };
};

type TabKey = 'lenders' | 'advisers';

// ---------- Adviser role classification ----------
// Mirrors packages/shared-aggregation/src/queries/adviserRole.ts. Kept in sync
// by the contract test there; the dropdown options below ARE the ADVISER_ROLES
// union. NULL = unclassified.

const ADVISER_ROLE_OPTIONS: { value: AdviserRole; label: string }[] = [
  { value: 'broker', label: 'Broker' },
  { value: 'operations', label: 'Operations' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'management', label: 'Management' },
  { value: 'general', label: 'General / shared inbox' },
  { value: 'inactive', label: 'Inactive' },
];

// Shared / general inbox local-parts → pre-suggest 'general'. Must match the
// backend GENERAL_INBOX_LOCALPART regex in adviserRole.ts.
const GENERAL_INBOX_LOCALPART = /^(info|hello|admin|accounts|support|enquiries|reception)@/i;

function suggestAdviserRole(email: string | null): AdviserRole | null {
  if (!email) return null;
  return GENERAL_INBOX_LOCALPART.test(email.trim()) ? 'general' : null;
}

// ---------- Adviser mapping modal ----------

const AdviserMappingModal: React.FC<{
  open: boolean;
  adviser: AdviserRow | null;
  brokers: BrokerCandidate[];
  onSave: (broker_display_names: string[]) => Promise<void>;
  onClose: () => void;
}> = ({ open, adviser, brokers, onSave, onClose }) => {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return brokers;
    return brokers.filter(
      (b) =>
        b.broker_display.toLowerCase().includes(q) ||
        b.broker_id.toLowerCase().includes(q)
    );
  }, [brokers, query]);

  const handleToggle = (displayName: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(displayName)) next.delete(displayName);
      else next.add(displayName);
      return next;
    });
  };

  const handleSave = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await onSave(Array.from(selected));
      setSelected(new Set());
      setQuery('');
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (!open || !adviser) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="w-full max-w-2xl rounded-lg border border-gray-700 bg-gray-900 p-6 shadow-2xl">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-white">
              Map adviser to VALUTTEN broker
            </h3>
            <p className="text-sm text-gray-400 mt-1">
              Adviser: {adviser.email || adviser.adviser_id} · {adviser.deal_count} deal
              {adviser.deal_count === 1 ? '' : 's'} in CRM
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Select ALL broker display-name variants that belong to this adviser
              (including &quot;(TRL PUR)&quot;, &quot;(Asset Tfr)&quot;, renamed forms).
              Backend expands each to both hash formats.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="relative mb-3">
          <Search className="w-4 h-4 text-gray-500 absolute left-3 top-3" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search brokers by display name or ID"
            className="w-full pl-9 pr-3 py-2 rounded-md bg-gray-800 border border-gray-700 text-white focus:outline-none focus:border-blue-500"
            autoFocus
          />
        </div>

        <div className="max-h-[400px] overflow-y-auto rounded-md border border-gray-700 divide-y divide-gray-800">
          {filtered.map((b) => {
            const key = b.broker_display;
            const active = selected.has(key);
            return (
              <button
                key={`${b.broker_display}-${b.broker_id}`}
                type="button"
                onClick={() => handleToggle(key)}
                className={`w-full flex items-center justify-between px-3 py-2 text-left hover:bg-gray-800 ${
                  active ? 'bg-blue-600/20 border-l-2 border-blue-500' : ''
                }`}
              >
                <div>
                  <div className="text-sm text-white">{b.broker_display}</div>
                  <div className="text-[0.7rem] text-gray-500 font-mono">{b.broker_id}</div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-gray-400">{b.loans} loans</span>
                  {active && <CheckCircle2 className="w-4 h-4 text-blue-400" />}
                </div>
              </button>
            );
          })}
          {filtered.length === 0 && (
            <div className="p-4 text-sm text-gray-500 text-center">No matches.</div>
          )}
        </div>

        {error && (
          <div className="mt-3 rounded-md border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300">
            {error}
          </div>
        )}

        <div className="flex justify-between items-center pt-4">
          <span className="text-xs text-gray-400">
            {selected.size} variant{selected.size === 1 ? '' : 's'} selected
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-md bg-gray-700 hover:bg-gray-600 text-white"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={submitting}
              className="px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
            >
              {submitting && <RefreshCw className="w-4 h-4 animate-spin" />}
              {submitting ? 'Saving…' : selected.size === 0 ? 'Clear mapping' : 'Save mapping'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------- Reset Sync Watermark modal (super_admin only) ----------
// Destructive operation — clears the incremental high-watermark so the next
// sync bootstraps a full backfill. Typing `RESET` is required to prevent a
// stray click from re-processing tens of thousands of deals. Keep the
// confirmation token as plain text so the action is self-documenting in
// screenshots / audit logs.

const ResetWatermarkModal: React.FC<{
  open: boolean;
  connection: CrmConnectionStatus | null;
  onConfirm: () => Promise<void>;
  onClose: () => void;
}> = ({ open, connection, onConfirm, onClose }) => {
  const [confirmText, setConfirmText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm();
      setConfirmText('');
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (!open || !connection) return null;

  const canConfirm = confirmText === 'RESET' && !submitting;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="w-full max-w-lg rounded-lg border border-red-500/30 bg-gray-900 p-6 shadow-2xl">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-red-400" />
            <h3 className="text-lg font-bold text-white">Reset sync watermark</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-3 text-sm text-gray-300 mb-6">
          <p>
            This clears the incremental-sync watermark for the{' '}
            <span className="font-mono text-white">{connection.provider}</span>{' '}
            connection. The next sync will re-fetch and MERGE every deal since
            the beginning of time.
          </p>
          <div className="rounded-md border border-gray-700 bg-gray-800 p-3 space-y-1 text-xs">
            <div>
              <span className="text-gray-500">Connection ID:</span>{' '}
              <span className="font-mono text-gray-200">{connection.connectionId}</span>
            </div>
            <div>
              <span className="text-gray-500">Last sync:</span>{' '}
              <span className="font-mono text-gray-200">
                {connection.lastSyncAt ?? 'never'}
              </span>
            </div>
            <div>
              <span className="text-gray-500">Last result:</span>{' '}
              <span className="font-mono text-gray-200">
                {connection.lastSyncResult ?? 'n/a'}
              </span>
            </div>
          </div>
          <p className="text-amber-300">
            Use this only when deal attribution is known to be wrong (e.g., a
            post-fix re-sync is needed). An audit log is written before the
            reset.
          </p>
        </div>

        <label className="block text-xs font-semibold text-gray-400 mb-1">
          Type <span className="font-mono text-white">RESET</span> to confirm
        </label>
        <input
          type="text"
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          className="w-full px-3 py-2 rounded-md bg-gray-800 border border-gray-700 text-white font-mono focus:outline-none focus:border-red-500"
          placeholder="RESET"
          autoFocus
        />

        {error && (
          <div className="mt-3 rounded-md border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 mt-6">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-md bg-gray-700 hover:bg-gray-600 text-white text-sm"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!canConfirm}
            className="px-4 py-2 rounded-md bg-red-600 hover:bg-red-700 text-white text-sm disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
          >
            {submitting && <RefreshCw className="w-4 h-4 animate-spin" />}
            {submitting ? 'Resetting…' : 'Reset watermark'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------- Main page ----------

const CrmMappingsPage: React.FC = () => {
  const { userData } = useOutletContext<OutletCtx>();
  const companyId = userData?.companyId ?? '';
  const isSuperAdmin = userData?.role === 'super_admin';
  // The adviser -> broker mapping decides which office owns a CRM deal, so only
  // an administrator with access to all offices may change it (the server
  // enforces this; the button only mirrors it).
  const canEditMapping =
    (isSuperAdmin || userData?.canSeeAllOffices === true) && !(userData?.allowedEntityCodes?.length);

  const {
    data,
    loading,
    error,
    refresh,
    saveAdviserBrokerIds,
    saveAdviserRole,
    resetSyncWatermark,
  } = useCrmMappings(companyId);

  const {
    connections,
    loading: connectionsLoading,
    refresh: refreshConnections,
  } = useCrmConnection(isSuperAdmin ? companyId : undefined);

  const [resetModal, setResetModal] = useState<{
    open: boolean;
    connection: CrmConnectionStatus | null;
  }>({ open: false, connection: null });

  const [activeTab, setActiveTab] = useState<TabKey>('lenders');
  const [adviserModal, setAdviserModal] = useState<{
    open: boolean;
    adviser: AdviserRow | null;
  }>({ open: false, adviser: null });

  const unmappedMycrm = data?.unmappedLenders.mycrm ?? [];
  const unmappedRc = data?.unmappedLenders.raw_commissions ?? [];
  const advisers = data?.advisers ?? [];
  const brokers = data?.brokerCandidates ?? [];

  // "is this adviser a broker?" — mirrors adviserBrokerPredicate in
  // packages/shared-aggregation/src/queries/adviserRole.ts. Backward-compatible:
  // unclassified (NULL role) + non-empty mapping still counts as a broker.
  const isBrokerAdviser = useCallback(
    (a: AdviserRow) =>
      a.adviser_role === 'broker' ||
      (a.adviser_role == null &&
        Array.isArray(a.valutten_broker_ids) &&
        a.valutten_broker_ids.length > 0),
    []
  );

  // Coverage badge counts BROKERS only (matches getMappingCoverage), so it
  // reads e.g. "6 / 8" brokers mapped — not "6 / 32" advisers.
  const brokerAdvisers = useMemo(
    () => advisers.filter(isBrokerAdviser),
    [advisers, isBrokerAdviser]
  );
  const mappedBrokerCount = useMemo(
    () =>
      brokerAdvisers.filter(
        (a) => a.valutten_broker_ids && a.valutten_broker_ids.length > 0
      ).length,
    [brokerAdvisers]
  );

  // Per-adviser role-save in flight (disables the row's dropdown).
  const [savingRoleId, setSavingRoleId] = useState<string | null>(null);
  const [roleError, setRoleError] = useState<string | null>(null);

  const handleSaveRole = useCallback(
    async (adviserId: string, role: AdviserRole | null) => {
      setSavingRoleId(adviserId);
      setRoleError(null);
      try {
        await saveAdviserRole({ adviser_id: adviserId, role });
      } catch (err) {
        setRoleError(err instanceof Error ? err.message : String(err));
      } finally {
        setSavingRoleId(null);
      }
    },
    [saveAdviserRole]
  );


  const handleSaveAdviser = useCallback(
    async (broker_display_names: string[]) => {
      if (!adviserModal.adviser) return;
      await saveAdviserBrokerIds({
        adviser_id: adviserModal.adviser.adviser_id,
        broker_display_names,
      });
    },
    [adviserModal, saveAdviserBrokerIds]
  );

  const handleResetWatermark = useCallback(async () => {
    if (!resetModal.connection) return;
    await resetSyncWatermark({
      connectionId: resetModal.connection.connectionId,
    });
    refreshConnections();
  }, [resetModal.connection, resetSyncWatermark, refreshConnections]);

  return (
    <div className="space-y-6 max-w-6xl">
      <PageHeader
        icon={Link2}
        title="CRM Mappings"
        subtitle="Map MyCRM advisers to VALUTTEN brokers, and review how your lender names are matched. Required for trail leakage detection."
        onRefresh={refresh}
        isRefreshing={loading}
      />

      {error && (
        <div className="rounded-md border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-300">
          {error}
        </div>
      )}

      <div className="flex gap-2 border-b border-gray-700">
        <button
          onClick={() => setActiveTab('lenders')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors inline-flex items-center gap-2 ${
            activeTab === 'lenders'
              ? 'border-blue-500 text-white'
              : 'border-transparent text-gray-400 hover:text-white'
          }`}
        >
          <Building2 className="w-4 h-4" />
          Lenders
          {(unmappedMycrm.length + unmappedRc.length) > 0 && (
            <span className="ml-1 px-1.5 py-0.5 rounded-full text-[0.65rem] bg-amber-500/20 text-amber-300 border border-amber-500/30">
              {unmappedMycrm.length + unmappedRc.length} unmapped
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('advisers')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors inline-flex items-center gap-2 ${
            activeTab === 'advisers'
              ? 'border-blue-500 text-white'
              : 'border-transparent text-gray-400 hover:text-white'
          }`}
        >
          <Users className="w-4 h-4" />
          Advisers
          <span
            className="ml-1 px-1.5 py-0.5 rounded-full text-[0.65rem] bg-gray-700 text-gray-300"
            title="Brokers mapped to VALUTTEN broker IDs / total classified brokers"
          >
            {mappedBrokerCount} / {brokerAdvisers.length} brokers
          </span>
        </button>
      </div>

      {loading && !data ? (
        <div className="flex items-center justify-center py-16 text-gray-400">
          <RefreshCw className="w-4 h-4 animate-spin mr-2" />
          Loading…
        </div>
      ) : activeTab === 'lenders' ? (
        <div className="space-y-6">
          {/* Lender mappings live in a registry shared by every VALUTTEN
              customer, so only VALUTTEN maintains them (crmMappingAction
              saveLenderAlias is founder-only). This tab is read-only. */}
          <p className="text-xs text-gray-400 max-w-3xl">
            VALUTTEN maintains lender name matching for all customers. Unmapped names below are
            reviewed by our team; contact support if a lender looks wrong or is missing.
          </p>
          {/* MyCRM lenders */}
          <div>
            <h3 className="text-sm font-semibold text-white mb-2">
              MyCRM lender strings · {unmappedMycrm.length} unmapped
            </h3>
            {unmappedMycrm.length === 0 ? (
              <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-4 text-sm text-emerald-300 inline-flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" />
                All MyCRM lender strings are mapped.
              </div>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-gray-700">
                <table className="w-full text-sm">
                  <thead className="bg-gray-900/60 text-gray-400 text-xs uppercase tracking-wider">
                    <tr>
                      <th className="text-left px-4 py-2">Lender string</th>
                      <th className="text-right px-4 py-2">Occurrences</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800">
                    {unmappedMycrm.map((l) => (
                      <tr key={l.lender_string} className="hover:bg-white/[0.02]">
                        <td className="px-4 py-2 text-white font-mono text-xs">{l.lender_string}</td>
                        <td className="px-4 py-2 text-right text-gray-300">{l.occurrences}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* raw_commissions lenders */}
          <div>
            <h3 className="text-sm font-semibold text-white mb-2">
              Commission data lender strings · {unmappedRc.length} unmapped
            </h3>
            {unmappedRc.length === 0 ? (
              <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-4 text-sm text-emerald-300 inline-flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" />
                All commission-data lender strings are mapped.
              </div>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-gray-700">
                <table className="w-full text-sm">
                  <thead className="bg-gray-900/60 text-gray-400 text-xs uppercase tracking-wider">
                    <tr>
                      <th className="text-left px-4 py-2">Lender string</th>
                      <th className="text-right px-4 py-2">Loans</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800">
                    {unmappedRc.map((l) => (
                      <tr key={l.lender_string} className="hover:bg-white/[0.02]">
                        <td className="px-4 py-2 text-white font-mono text-xs">{l.lender_string}</td>
                        <td className="px-4 py-2 text-right text-gray-300">{l.loan_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-xs text-gray-400 max-w-3xl">
            Classify each MyCRM adviser by role. Only{' '}
            <span className="text-white font-medium">Brokers</span> are mapped to
            VALUTTEN broker IDs, attributed commission/activity, and counted in
            the Scorecard, Funnel, and coverage. Operations, marketing,
            management, general/shared inboxes, and inactive advisers are
            excluded. Shared inboxes (info@, admin@, hello@…) are pre-suggested
            as <span className="text-white font-medium">General</span> — confirm
            with the dropdown.
          </p>
          {roleError && (
            <div className="rounded-md border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300">
              {roleError}
            </div>
          )}
          <div className="overflow-x-auto rounded-lg border border-gray-700">
            <table className="w-full text-sm">
              <thead className="bg-gray-900/60 text-gray-400 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-2">Adviser</th>
                  <th className="text-left px-4 py-2">Role</th>
                  <th className="text-right px-4 py-2">Deals</th>
                  <th className="text-left px-4 py-2">Current mapping</th>
                  <th className="text-right px-4 py-2">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {advisers.map((a) => {
                  const mappedCount = a.valutten_broker_ids?.length ?? 0;
                  const suggested = suggestAdviserRole(a.email);
                  // Mapping is only meaningful for brokers. Unclassified (NULL)
                  // is allowed to map for backward compatibility.
                  const canMap = a.adviser_role === 'broker' || a.adviser_role == null;
                  const isSavingRole = savingRoleId === a.adviser_id;
                  return (
                    <tr key={a.adviser_id} className="hover:bg-white/[0.02]">
                      <td className="px-4 py-2">
                        <div className="text-white">{a.email || '—'}</div>
                        <div className="text-[0.7rem] text-gray-500 font-mono">
                          MyCRM id: {a.adviser_id}
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-2">
                          <select
                            value={a.adviser_role ?? ''}
                            disabled={isSavingRole}
                            onChange={(e) => {
                              const v = e.target.value;
                              void handleSaveRole(
                                a.adviser_id,
                                v === '' ? null : (v as AdviserRole)
                              );
                            }}
                            className="rounded-md bg-gray-800 border border-gray-700 text-white text-xs px-2 py-1 focus:outline-none focus:border-blue-500 disabled:opacity-50"
                          >
                            <option value="">Unclassified</option>
                            {ADVISER_ROLE_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                          {isSavingRole && (
                            <RefreshCw className="w-3.5 h-3.5 text-gray-400 animate-spin" />
                          )}
                          {a.adviser_role == null && suggested && (
                            <span
                              className="text-[0.65rem] text-amber-300/90"
                              title="Suggested from email — not saved until you choose it"
                            >
                              suggest: {suggested}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-2 text-right text-gray-300">{a.deal_count}</td>
                      <td className="px-4 py-2">
                        {!canMap ? (
                          <span className="text-xs text-gray-500">n/a</span>
                        ) : mappedCount === 0 ? (
                          <span className="text-xs text-amber-400">Unmapped</span>
                        ) : (
                          <span className="text-xs text-emerald-400 font-mono">
                            {mappedCount} broker_id{mappedCount === 1 ? '' : 's'}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <button
                          onClick={() => setAdviserModal({ open: true, adviser: a })}
                          disabled={!canMap || !canEditMapping}
                          title={
                            !canEditMapping
                              ? 'Only an administrator with access to all offices can change adviser mappings'
                              : canMap
                                ? undefined
                                : 'Only advisers classified as Broker can be mapped to a VALUTTEN broker'
                          }
                          className="px-3 py-1 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          {mappedCount === 0 ? 'Map' : 'Edit'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {advisers.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-6 text-center text-gray-500 text-sm">
                      No advisers synced yet. Run a CRM sync first.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {isSuperAdmin && (
        <div className="mt-8 rounded-lg border border-gray-700 bg-gray-800/40 p-5">
          <div className="flex items-center gap-2 mb-3">
            <RotateCcw className="w-4 h-4 text-amber-400" />
            <h3 className="text-sm font-semibold text-white">
              Sync state recovery
            </h3>
            <span className="ml-1 px-1.5 py-0.5 rounded-full text-[0.65rem] bg-red-500/15 text-red-300 border border-red-500/30">
              super_admin
            </span>
          </div>
          <p className="text-xs text-gray-400 mb-4 max-w-2xl">
            Clear the incremental-sync watermark to force a full re-backfill on
            the next sync. Use this when deal attribution is known to be wrong
            and you need historical deals re-normalised through the current
            parsing logic.
          </p>
          {connectionsLoading ? (
            <div className="text-sm text-gray-500">Loading connections…</div>
          ) : connections.length === 0 ? (
            <div className="text-sm text-gray-500">No CRM connections.</div>
          ) : (
            <div className="space-y-2">
              {connections.map((c) => (
                <div
                  key={c.connectionId}
                  className="flex items-center justify-between rounded-md border border-gray-700 bg-gray-900/50 px-4 py-3"
                >
                  <div className="text-xs space-y-0.5">
                    <div className="font-mono text-gray-200">
                      {c.provider}{' '}
                      <span className="text-gray-500">
                        · {c.connectionId.slice(0, 12)}…
                      </span>
                    </div>
                    <div className="text-gray-500">
                      last sync {c.lastSyncAt ?? 'never'} ·{' '}
                      {c.lastSyncResult ?? 'n/a'}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      setResetModal({ open: true, connection: c })
                    }
                    className="px-3 py-1.5 rounded-md border border-red-500/30 bg-red-500/10 hover:bg-red-500/20 text-red-300 text-xs inline-flex items-center gap-1.5"
                  >
                    <RotateCcw className="w-3 h-3" />
                    Reset watermark
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <AdviserMappingModal
        open={adviserModal.open}
        adviser={adviserModal.adviser}
        brokers={brokers}
        onSave={handleSaveAdviser}
        onClose={() => setAdviserModal({ open: false, adviser: null })}
      />
      <ResetWatermarkModal
        open={resetModal.open}
        connection={resetModal.connection}
        onConfirm={handleResetWatermark}
        onClose={() => setResetModal({ open: false, connection: null })}
      />
    </div>
  );
};

export default CrmMappingsPage;
